import { computed } from "vue";
import type { Route, SidebarOptionName } from "~/types/UpNavRoutesType";
import { upNavRouteConfig } from "~/constants/upNavRouteConfig.ts";

export const useUpNavRoutes = () => {
  //  Flatten nested child routes with parent route context
  const flattenRoutesWithParentContext = (
    routes: Route[],
    context: Record<string, any> = {},
    parentIds: number[] = [],
    counter: { value: number } = { value: 0 },
  ): Route[] => {
    return routes.reduce((result, route) => {
      const { childRoutes, ...parentRouteData } = route;

      // Generate unique ID and type-specific ID
      counter.value++;
      const id = counter.value;
      const typeIdKey = `${route.type}Id`;
      // Get sidebarOptionName if route is a sidebar type
      const sidebarOptionName = route.name || context.sidebarOptionName;
      const upsellPath = (route.upsellPath || context.upsellPath) ?? "";

      // Flatten each child route with parent route context - type IDS and parent IDs
      const childRoutesWithParentContext =
        childRoutes?.map((child) =>
          flattenRoutesWithParentContext(
            [child],
            { ...context, [typeIdKey]: id, sidebarOptionName, upsellPath },
            [...parentIds, id],
            counter,
          ),
        ) ?? [];

      // Build route with ID, parent route data, context:type IDs, parentIDs and immediate child routes only
      const flatRoute: Route = {
        ...parentRouteData,
        ...context,
        id,
        [typeIdKey]: id,
        parentIds,
        sidebarOptionName,
        upsellPath,
        childRoutes: childRoutesWithParentContext.map((r) => r[0] as Route),
      };

      return [...result, flatRoute, ...childRoutesWithParentContext.flat()];
    }, [] as Route[]);
  };

  // Memoized flattened routes with parent context.
  const upNavRoutes = computed(() => {
    return flattenRoutesWithParentContext(Object.values(upNavRouteConfig));
  });

  // Route maps for quick lookups.
  const {
    upNavRoutesById,
    upNavSidebarOptions,
    upNavNavbarOptions,
    upNavbarDropdownOptions,
    upNavRoutesByPath,
  } = upNavRoutes.value.reduce(
    (maps, route) => {
      maps.upNavRoutesById.set(route.id!, route);

      if (route.path) {
        maps.upNavRoutesByPath.set(route.path!, route);
      }
      if (route.upsellPath) {
        maps.upNavRoutesByPath.set(route.upsellPath!, route);
      }

      if (!maps.upNavSidebarOptions.has(route.sidebarOptionName!)) {
        maps.upNavSidebarOptions.set(route.sidebarOptionName!, route);
      }

      if (route.type === "navbarOption") {
        const existingNavbarOptions =
          maps.upNavNavbarOptions.get(route.sidebarOptionName!) ?? [];
        maps.upNavNavbarOptions.set(route.sidebarOptionName!, [
          ...existingNavbarOptions,
          route,
        ]);
      }

      if (route.type === "navbarDropdownOption") {
        const existingNavbarDropdownOptions =
          maps.upNavbarDropdownOptions.get(route.navbarOptionId!) ?? [];
        maps.upNavbarDropdownOptions.set(route.navbarOptionId!, [
          ...existingNavbarDropdownOptions,
          route,
        ]);
      }
      return maps;
    },
    {
      upNavRoutesById: new Map<number, Route>(),
      upNavRoutesByPath: new Map<string, Route>(),
      upNavSidebarOptions: new Map<SidebarOptionName, Route>(),
      upNavNavbarOptions: new Map<SidebarOptionName, Route[]>(),
      upNavbarDropdownOptions: new Map<number, Route[]>(),
    },
  );

  // Get parent routes by their IDs from indexed map.
  const getUpNavParentRoutes = (parentIds: number[]) => {
    return parentIds.map((id) => upNavRoutesById.get(id));
  };

  return {
    upNavRoutes,
    upNavRoutesById,
    upNavRoutesByPath,
    upNavSidebarOptions,
    upNavNavbarOptions,
    upNavbarDropdownOptions,
    getUpNavParentRoutes,
  };
};
